import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, CheckCircle, MessageSquare, Send, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export default function ClientDetailPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgContent, setMsgContent] = useState('');
  const [showContract, setShowContract] = useState(false);
  const [contractForm, setContractForm] = useState({ service: '', price: '', start_date: '', duration: '6' });
  const [resending, setResending] = useState(false);

  const load = () => api('get', `/api/admin/clients/${id}`).then(r => setClient(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const sendMsg = async () => {
    if (!msgContent.trim()) return;
    await api('post', `/api/admin/messages/${id}`, { content: msgContent });
    setMsgContent('');
    toast.success('Bericht verstuurd');
    load();
  };

  const createContract = async (e) => {
    e.preventDefault();
    await api('post', '/api/admin/contracts', { ...contractForm, company_id: id });
    toast.success('Contract aangemaakt');
    setShowContract(false);
    load();
  };

  const resendInvite = async () => {
    if (!user?.id) {
      toast.error('Geen gebruiker gekoppeld aan deze klant');
      return;
    }
    setResending(true);
    try {
      const r = await api('post', `/api/admin/clients/${user.id}/resend-invite`);
      if (r.data.email_status === 'sent') {
        toast.success(`Uitnodiging verstuurd naar ${r.data.email}`);
      } else if (r.data.email_status === 'queued_after_failure') {
        toast.warning(`SMTP traag — uitnodiging staat in retry queue (${r.data.email_error || 'onbekende fout'})`);
      } else {
        toast.info(`Status: ${r.data.email_status}`);
      }
    } catch (e) {
      toast.error(`Verzenden mislukt: ${e.response?.data?.detail || e.message}`);
    } finally {
      setResending(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#D4A017] border-t-transparent rounded-full animate-spin" /></div>;
  if (!client) return <div className="text-white">Klant niet gevonden</div>;

  const { company, user, contract, onboarding, meeting, messages } = client;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/clients" className="text-[#64748b] hover:text-[#D4A017] transition-colors"><ArrowLeft size={20} /></Link>
        <div>
          <h1 className="text-2xl font-bold text-white">{company?.name}</h1>
          <p className="text-[#64748b] text-sm">{user?.email}</p>
        </div>
        <span className={`ml-auto text-xs px-3 py-1.5 rounded-full font-bold ${company?.onboarding_status === 'complete' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
          {company?.onboarding_status === 'complete' ? 'Onboarding voltooid' : 'Onboarding lopend'}
        </span>
        {company?.onboarding_status !== 'complete' && user?.id && (
          <button
            data-testid="resend-invite-btn"
            onClick={resendInvite}
            disabled={resending}
            className="text-xs px-3 py-1.5 rounded-full font-bold bg-[#D4A017]/15 text-[#D4A017] hover:bg-[#D4A017]/25 disabled:opacity-50 flex items-center gap-1.5"
            title="Verstuur welkomstmail opnieuw"
          >
            {resending ? <><Loader2 size={11} className="animate-spin" /> Bezig…</> : <><Mail size={11} /> Verstuur uitnodiging opnieuw</>}
          </button>
        )}
        {user?.id && (
          <Link
            data-testid="full-profile-btn"
            to={`/admin/customers/${user.id}`}
            className="text-xs px-3 py-1.5 rounded-full font-bold bg-white text-[#0f172a] border border-white/10 hover:border-[#D4A017]"
          >
            Volledig profile →
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Company info */}
        <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6 space-y-3">
          <h3 className="text-[#D4A017] font-bold text-xs uppercase tracking-wider">Bedrijfsinfo</h3>
          {[
            ['Bedrijf', company?.name],
            ['Website', company?.website],
            ['Contact', company?.contact_name],
            ['Telefoon', company?.phone],
            ['Dienst', company?.service],
            ['Branche', onboarding?.s1_industry],
          ].map(([k, v]) => v && (
            <div key={k} className="flex gap-3">
              <span className="text-[#475569] text-sm w-24 flex-shrink-0">{k}:</span>
              <span className="text-white text-sm">{v}</span>
            </div>
          ))}
        </div>

        {/* Onboarding progress */}
        <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6">
          <h3 className="text-[#D4A017] font-bold text-xs uppercase tracking-wider mb-4">Onboarding voortgang</h3>
          <div className="h-2 bg-white/10 rounded-full mb-3">
            <div className="h-full bg-[#D4A017] rounded-full" style={{ width: `${Math.round(((company?.onboarding_step || 0) / 6) * 100)}%` }} />
          </div>
          <p className="text-[#94a3b8] text-sm mb-4">Stap {company?.onboarding_step || 0} van 6 voltooid</p>
          {['s2_goals', 's3_services'].map(k => onboarding?.[k]?.length > 0 && (
            <div key={k} className="mb-3">
              <p className="text-[#475569] text-xs mb-2">{k === 's2_goals' ? 'Doelen' : 'Diensten'}:</p>
              <div className="flex flex-wrap gap-1">{(onboarding[k] || []).map(i => <span key={i} className="bg-white/10 text-[#94a3b8] text-xs px-2 py-0.5 rounded">{i}</span>)}</div>
            </div>
          ))}
        </div>

        {/* Contract */}
        <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#D4A017] font-bold text-xs uppercase tracking-wider">Contract</h3>
            <button onClick={() => setShowContract(!showContract)} className="text-[#D4A017] text-xs hover:underline">+ Aanmaken</button>
          </div>
          {showContract && (
            <form onSubmit={createContract} className="space-y-3 mb-4">
              {[['Dienst', 'service', 'text'],['Prijs/maand (€)', 'price', 'number'],['Startdatum', 'start_date', 'date']].map(([l, f, t]) => (
                <div key={f}><label className="text-[#94a3b8] text-xs mb-1 block">{l}</label>
                  <input type={t} value={contractForm[f]} onChange={e => setContractForm(c => ({...c, [f]: e.target.value}))} required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4A017]" /></div>
              ))}
              <div><label className="text-[#94a3b8] text-xs mb-1 block">Looptijd</label>
                <select value={contractForm.duration} onChange={e => setContractForm(c => ({...c, duration: e.target.value}))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4A017]">
                  {['3','6','9','12'].map(d => <option key={d} value={d}>{d} maanden</option>)}
                </select></div>
              <button type="submit" className="w-full bg-[#D4A017] text-[#0f172a] font-bold py-2 rounded-lg text-sm">Contract aanmaken</button>
            </form>
          )}
          {contract ? (
            <div className="space-y-2 text-sm">
              {[['Dienst', contract.service],['Prijs', `€${contract.price}/mnd`],['Start', contract.start_date],['Looptijd', `${contract.duration} mnd`],['Status', contract.status]].map(([k,v]) => (
                <div key={k} className="flex justify-between"><span className="text-[#475569]">{k}:</span><span className="text-white font-medium">{v}</span></div>
              ))}
            </div>
          ) : <p className="text-[#475569] text-sm">Geen contract</p>}
        </div>

        {/* Meeting */}
        <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6">
          <h3 className="text-[#D4A017] font-bold text-xs uppercase tracking-wider mb-4">Kick-off Meeting</h3>
          {meeting ? (
            <div className="space-y-2 text-sm">
              {[['Datum', meeting.date],['Tijd', meeting.time],['Link', meeting.meeting_link]].map(([k,v]) => (
                <div key={k} className="flex gap-3"><span className="text-[#475569] w-16 flex-shrink-0">{k}:</span><span className="text-white">{v}</span></div>
              ))}
              <span className="inline-block mt-2 bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">Ingepland</span>
            </div>
          ) : <p className="text-[#475569] text-sm">Nog niet gepland</p>}
        </div>
      </div>

      {/* Messages */}
      <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6 mt-6">
        <h3 className="text-[#D4A017] font-bold text-xs uppercase tracking-wider mb-4">Berichten</h3>
        <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
          {[...(messages || [])].reverse().map(m => (
            <div key={m.id} className={`p-3 rounded-xl text-sm ${m.from_role === 'admin' ? 'bg-[#D4A017]/10 border border-[#D4A017]/20' : 'bg-white/5'}`}>
              <p className="text-[#94a3b8] text-xs mb-1">{m.from_role === 'admin' ? 'Jij' : 'Klant'} · {m.created_at?.slice(0, 16)}</p>
              <p className="text-white">{m.content}</p>
            </div>
          ))}
          {!messages?.length && <p className="text-[#475569] text-sm">Geen berichten</p>}
        </div>
        <div className="flex gap-3">
          <input value={msgContent} onChange={e => setMsgContent(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMsg()} placeholder="Stuur een bericht naar de klant..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017] text-sm" />
          <button onClick={sendMsg} className="bg-[#D4A017] text-[#0f172a] font-bold px-4 py-3 rounded-xl"><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}
